import * as React from 'react';
import { Search } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import {
  CheckRow,
  SearchField,
  SegmentedControl,
  Sheet,
  SheetStat,
  WorkspaceButton,
  WorkspaceField,
  WorkspaceSelect,
} from '@/components/ui/workspace';
import {
  useAvailableProducts,
  type AvailableProduct,
  type ProductChoice,
} from '@/lib/api/vendor-intelligence';
import { useVendorLists } from '@/lib/api/vendor-lists';
import { evidenceOf, pickerSections, plural, productTitle } from './vi-logic';

/**
 * Add vendors (T-0267; E14-design §3): two ways in.
 *   - From a list: pick one of the firm's curated vendor lists; its
 *     products are ticked, in the list's order, and the engagement records
 *     the list each came from (for the report's method section).
 *   - All vendors: search every knowledge-base product. Products with
 *     evidence in this engagement's modules come first, most evidence
 *     first; the ones with none sit behind "Show …" until asked for or
 *     searched. They can still be added, and rate Unknown.
 * The choice of source and the search stay put at the top; only the
 * products scroll.
 */

type Mode = 'list' | 'all';

export function VendorPicker({
  engagementId,
  onClose,
  onAdd,
  busy,
  error,
}: {
  engagementId: string;
  onClose: () => void;
  onAdd: (choices: ProductChoice[]) => void;
  busy: boolean;
  error: unknown;
}) {
  const lists = useVendorLists(false);
  const [mode, setMode] = React.useState<Mode>('list');
  const [listId, setListId] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const [ticked, setTicked] = React.useState<Set<string>>(() => new Set());
  const [showNoEvidence, setShowNoEvidence] = React.useState(false);

  // No curated list yet: start on All vendors.
  React.useEffect(() => {
    if (lists.data && lists.data.lists.length === 0) setMode('all');
  }, [lists.data]);

  const source = mode === 'list' ? listId : null;
  const available = useAvailableProducts(engagementId, source, mode === 'all' || listId !== null);

  // A list's products start ticked (the ones that can still be added).
  React.useEffect(() => {
    if (mode !== 'list' || !available.data?.list) return;
    setTicked(
      new Set(
        available.data.products.filter((p) => p.addable && !p.in_scope).map((p) => p.product_id),
      ),
    );
  }, [mode, available.data]);

  const switchMode = (next: Mode) => {
    setMode(next);
    setTicked(new Set());
    setQuery('');
  };

  const q = query.trim().toLowerCase();
  const searching = mode === 'all' && q.length > 0;
  const products = (available.data?.products ?? []).filter(
    (p) =>
      mode === 'list' || q.length === 0 || `${p.vendor.name} ${p.name}`.toLowerCase().includes(q),
  );
  const sections = pickerSections(products);
  const toggle = (id: string) =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const add = () =>
    onAdd(
      [...ticked].map((product_id) => ({
        product_id,
        from_list_id: mode === 'list' ? listId : null,
      })),
    );

  const row = (p: AvailableProduct) => (
    <ProductRow
      key={p.product_id}
      product={p}
      checked={ticked.has(p.product_id)}
      onToggle={() => toggle(p.product_id)}
    />
  );

  let body: React.ReactNode;
  if (available.isFetching && !available.data) {
    body = (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  } else if (available.error) {
    body = <ErrorMessage error={available.error} />;
  } else if (mode === 'list' && listId === null) {
    body = (
      <p className="m-0 py-6 text-center text-body-sm text-ink-500">
        Choose a list to see its vendors.
      </p>
    );
  } else if (products.length === 0) {
    body = (
      <p className="m-0 py-6 text-center text-body-sm text-ink-500">
        {mode === 'list' ? 'This list has no products.' : 'No product matches.'}
      </p>
    );
  } else if (mode === 'list') {
    body = products.map(row);
  } else {
    const noEvidenceOpen = searching || showNoEvidence || sections.withEvidence.length === 0;
    body = (
      <>
        {sections.withEvidence.length > 0 ? (
          <ProductSection
            title="With evidence in this engagement's modules"
            count={sections.withEvidence.length}
          >
            {sections.withEvidence.map(row)}
          </ProductSection>
        ) : null}
        {sections.noEvidence.length === 0 ? null : noEvidenceOpen ? (
          <ProductSection
            title="No evidence yet"
            count={sections.noEvidence.length}
            hint="They can still be added; their ratings will be Unknown until the knowledge base has evidence."
          >
            {sections.noEvidence.map(row)}
          </ProductSection>
        ) : (
          <div className="py-3">
            <WorkspaceButton size="sm" variant="tertiary" onClick={() => setShowNoEvidence(true)}>
              Show {plural(sections.noEvidence.length, 'vendor')} with no evidence yet
            </WorkspaceButton>
          </div>
        )}
      </>
    );
  }

  return (
    <Sheet
      open
      onOpenChange={(open) => (open ? undefined : onClose())}
      title="Add vendors"
      description="Choose the knowledge-base products this engagement weighs."
      widthClassName="w-[min(720px,94vw)]"
      summary={
        ticked.size > 0 ? (
          <SheetStat value={ticked.size.toLocaleString('en-US')} label="selected" />
        ) : (
          <span className="text-ink-500">Tick the vendors to add</span>
        )
      }
      actions={
        <>
          <WorkspaceButton size="md" variant="tertiary" onClick={onClose}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton
            size="md"
            variant="primary"
            disabled={ticked.size === 0 || busy}
            onClick={add}
          >
            {ticked.size > 0 ? `Add ${plural(ticked.size, 'vendor')}` : 'Add vendors'}
          </WorkspaceButton>
        </>
      }
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="flex flex-none flex-col gap-3 border-b border-ink-100 px-5 py-4">
          <SegmentedControl
            aria-label="Add vendors"
            className="flex-none self-start"
            value={mode}
            onChange={switchMode}
            options={[
              { value: 'list', label: 'From a list' },
              { value: 'all', label: 'All vendors' },
            ]}
          />
          {mode === 'list' ? (
            <WorkspaceField
              label="Vendor list"
              help={
                lists.data && lists.data.lists.length === 0
                  ? 'No vendor list yet. Curators make them in Firm settings → Vendor knowledge → Vendor lists.'
                  : 'Lists are curated in Firm settings → Vendor knowledge → Vendor lists.'
              }
            >
              {(id, describedBy) => (
                <WorkspaceSelect
                  id={id}
                  aria-describedby={describedBy}
                  value={listId ?? ''}
                  onChange={(e) => {
                    setListId(e.target.value || null);
                    setTicked(new Set());
                  }}
                >
                  <option value="">Choose a list</option>
                  {(lists.data?.lists ?? []).map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name} ({plural(l.product_count, 'product')})
                    </option>
                  ))}
                </WorkspaceSelect>
              )}
            </WorkspaceField>
          ) : (
            <SearchField
              value={query}
              onChange={setQuery}
              aria-label="Search vendors and products"
              placeholder="Search vendors and products"
              icon={<Search className="h-3.5 w-3.5" aria-hidden="true" />}
              wrapperClassName="w-full gap-1.5 px-2"
            />
          )}
          {mode === 'list' && available.data?.list?.description ? (
            <p className="m-0 text-body-sm text-ink-500">{available.data.list.description}</p>
          ) : null}
        </div>
        <div
          role="group"
          aria-label="Products"
          className="min-h-0 flex-1 overflow-y-auto px-5 pb-4"
        >
          {body}
        </div>
        {error ? (
          <div className="flex-none border-t border-ink-100 px-5 py-3">
            <ErrorMessage error={error} />
          </div>
        ) : null}
      </div>
    </Sheet>
  );
}

function ProductSection({
  title,
  count,
  hint,
  children,
}: {
  title: string;
  count: number;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <section aria-label={title} className="pt-3">
      <div className="sticky top-0 z-10 flex items-baseline gap-2 bg-paper-0 py-1.5">
        <h3 className="m-0 text-meta font-semibold text-ink-700">{title}</h3>
        <span className="font-mono text-[11px] text-ink-500">{count.toLocaleString('en-US')}</span>
      </div>
      {hint ? <p className="m-0 mb-1 text-meta text-ink-500">{hint}</p> : null}
      {children}
    </section>
  );
}

function ProductRow({
  product: p,
  checked,
  onToggle,
}: {
  product: AvailableProduct;
  checked: boolean;
  onToggle: () => void;
}) {
  const blocked = p.in_scope || !p.addable;
  const { title, vendor } = productTitle(p);
  const status = p.in_scope
    ? 'In scope already'
    : !p.addable
      ? 'Retired in the knowledge base'
      : evidenceOf(p) === 0
        ? 'No evidence yet'
        : [
            `${plural(p.confirmed_items, 'confirmed item')} in these modules`,
            p.answers > 0 ? plural(p.answers, 'requirement answer') : null,
          ]
            .filter(Boolean)
            .join(' · ');
  return (
    <CheckRow checked={checked && !blocked} onToggle={onToggle} disabled={blocked}>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-ink-900">{title}</span>
        <span className="block text-meta text-ink-500">
          {vendor ? `${vendor} · ` : ''}
          {status}
        </span>
      </span>
    </CheckRow>
  );
}
